//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { LayoutContext, AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import useCountryStateCity from '../../common/hooks/useCountryStateCity';
import {
    CONFIRM_DIALOG_TITLE_DELETE_PROFILE_PHOTO,
    CONFIRM_DIALOG_MESSAGE_DELETE_PROFILE_PHOTO,
    // UPLOADED_VIDEO_STATUS_APPROVED,
    // UPLOADED_VIDEO_STATUS_FAILED,
    UPLOADED_VIDEO_STATUS_PROCESSING,
    SNACK_BAR_MESSAGE_TEXT_MODERATION_CHECK_FAILED,
    CONFIRM_DIALOG_TITLE_CANCEL,
    CONFIRM_DIALOG_MESSAGE_CANCEL,
} from '../../common/constants';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { updateUserData, uploadProfilePicture, deleteProfilePicture, resetJsSeResults } from '../../common/services/mySettingsServices';
import {
    //  detectInappropriatePicture,
    extractBadContentFromResult,
    moderateUserTextInput,
} from '../../common/sightEngineHelper';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';
import AfitdEditDialog from '../../common/elemental/AfitdEditDialog';
// import AfitdAutocompleteLite from '../../common/elemental/AfitdAutocompleteLite';
import AfitdDateFormatAutocomplete from '../../common/elemental/AfitdDateFormatAutocomplete';
import AfitdProfilePicCropper from '../../common/elemental/AfitdProfilePicCropper';
import AfitdCountryAutocomplete from '../../common/elemental/AfitdCountryAutocomplete';
import AfitdStateAutocomplete from '../../common/elemental/AfitdStateAutocomplete';
import AfitdCityAutocomplete from '../../common/elemental/AfitdCityAutocomplete';
import AfitdAvatar from '../../common/elemental/AfitdAvatar';
import AfitdLinearProgress from '../../common/elemental/AfitdLinearProgress';
import AfitdAvatarWithRing from '../../common/elemental/AfitdAvatarWithRing';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';
import AfitdRouterPrompt from '../../common/elemental/AfitdRouterPrompt';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';
import LoadingGif from '../../assets/loading.gif';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import cx from 'clsx';
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        avatarPlaceholder: {
            width: theme.spacing(8),
            height: theme.spacing(8),
            borderRadius: '100%',
        },
        avatarPlaceholderBox: {
            width: 104,
            height: 104,
            background: '#FFFFFF',
            border: '1px dashed #D5D7DD',
            borderRadius: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            boxSizing: 'border-box',
        },
        btnBox: {
            padding: 10,
            paddingTop: 30,
            [theme.breakpoints.down('sm')]: { paddingLeft: 0 },
        },
        instructionText: {
            marginTop: 5,
            color: 'rgba(17, 30, 20, 0.4)',
        },
        inputBoxContainer: {
            display: 'flex',
            margin: '10px 0',
            [theme.breakpoints.down('sm')]: { flexDirection: 'column', margin: '0px' },
        },
        inputBox: {
            flex: 1,
            padding: '0 5px',
            [theme.breakpoints.down('sm')]: { padding: '12px 0px' },
        },
        label: {
            padding: '5px 0',
            fontWeight: 'bold',
        },
        dropDown: {
            fontSize: '16px',
            color: '#1D8A8B',
            border: '1px solid #D5D7DD',
            borderRadius: 4,
        },
        focusDropDown: {
            fontSize: '16px',
            color: '#1D8A8B',
            border: '2px solid #1D8A8B',
            borderRadius: 4,
        },
        buttonBox: {
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '10px 0',
            [theme.breakpoints.down('sm')]: {
                flexDirection: 'column-reverse',
                justifyContent: 'center',
                padding: '0px',
            },
        },
        heading: {
            fontWeight: 'bold',
        },

        rightMarginToLeftButton: { marginRight: 16 },
        generalAvatarContainer: { display: 'flex', margin: '30px 0', [theme.breakpoints.down('sm')]: { flexDirection: 'column' } },
        hidden: { display: 'none' },
        cropContainer: { height: 250, width: '100%', textAlign: 'center' },
        imgBeforeCropContainer: { display: 'flex', justifyContent: 'center' },
        uploadLinearProgressContainer: { marginTop: 30, padding: '0 20px' },
        generalCancelButton: { marginRight: 16, [theme.breakpoints.down('sm')]: { width: '100%', marginRight: 0 } },
        generalSaveButton: { [theme.breakpoints.down('sm')]: { width: '100%', marginBottom: 16 } },
        firstNameError: { color: 'red' },
        lastNameError: { color: 'red' },
        countryError: { color: 'red' },
        stateError: { color: 'red' },
        cityError: { color: 'red' },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        isDirty,
        // setIsDirty,
        data,
        // country,
        // state,
        // city,
        countryProps,
        stateLabelProps,
        stateProps,
        cityProps,
        classes,
        handleChangeFields,
        handleSaveButton,
        handleClickConfirmCancelModalYesButton,
        handleUploadPic,
        handleDeletePic,
        handleCloseCropModal,
        // currentUser,
        confirmModalOpen,
        setConfirmModalOpen,
        confirmDeletePhotoModalOpen,
        setConfirmDeletePhotoModalOpen,
        cropModalOpen,
        // setCropModalOpen,
        handleChangePicUploadInput,
        // handleChangeAutoComplete,
        handleChangeDateFormat,
        firstNameError,
        lastNameError,
        setBlob,
        tempImage,
        progress,
        pendingAccountSetup,
        countryError,
        // setCountryError,
        stateError,
        // setStateError,
        cityError,
        // setCityError,
        openBadWordsDialog,
        // setOpenBadWordsDialog,
        badWords,
        // setBadWords,
        clearOutBadWords,
        saveLoading,
        openBadImageDialog,
        setOpenBadImageDialog,
        badImageDialogMessage,
        isPictureProcessing,
        userId,
        setSnackbarMessage,
        setSnackbarErrorMessage,
    } = params;
    const { rightMarginToLeftButton: rightMarginToLeftButtonCls, hidden: hiddenCls } = classes;
    const { firstName, lastName, middleName, /*country, city, state,*/ /*timeFormat,*/ dateFormat, /*language,*/ profilePicUrl } = data;

    // const LanguagesList = ['English', 'Spanish', 'Mandarin Chinese'];

    // const isButtonActive =
    //     firstName === currentUser.firstName &&
    //     lastName === currentUser.lastName &&
    //     middleName === currentUser.middleName &&
    //     profilePicUrl === currentUser.profilePicUrl &&
    //     country === currentUser.country &&
    //     city === currentUser.city &&
    //     state === currentUser.state &&
    //     // timeFormat === currentUser.timeFormat &&
    //     dateFormat === currentUser.dateFormat;
    // //  &&
    // // language === currentUser.language;

    const isProfilePicture = profilePicUrl || false;

    const badWordsList = badWords?.profanity?.join(', ');

    const defns = {
        generalAvatarContainer: {},
        profilePic: {
            photoSize: 96,
            borderSize: 4,
            placeholderFontSize: 30,
            src: profilePicUrl,
        },
        loadingPictureAvatar: {
            photoSize: 96,
            borderSize: 4,
            placeholderFontSize: 30,
            src: LoadingGif,
        },
        btnBox: {},
        generalUploadPicButton: {
            className: rightMarginToLeftButtonCls,
            color: 'secondary',
            title: isProfilePicture ? 'Upload new photo' : 'Upload photo',
            component: 'label',
            disabled: isPictureProcessing,
        },

        generalDeletePicButton: {
            className: cx({ [hiddenCls]: !isProfilePicture }),
            onClick: () => setConfirmDeletePhotoModalOpen(!confirmDeletePhotoModalOpen),
            disabled: isPictureProcessing,
        },
        generalFirstName: { onChange: handleChangeFields, name: 'firstName', value: firstName, placeholder: 'First Name', error: firstNameError },
        generalMiddleName: { onChange: handleChangeFields, name: 'middleName', value: middleName, placeholder: 'Middle Name' },
        generalLastName: { onChange: handleChangeFields, name: 'lastName', value: lastName, placeholder: 'Last Name', error: lastNameError },
        instructionText: { variant: 'body2' },
        inputBox: {},
        inputBoxContainer: {},
        // generalLanguage: {
        //     onChange: handleChangeAutoComplete,
        //     options: LanguagesList,
        //     value: language,
        //     placeholder: 'Language',
        //     id: 'language',
        // },
        generalDateFormat: {
            onChange: handleChangeDateFormat,
            value: dateFormat,
            id: 'dateFormat',
        },
        // generalTimeFormat: {
        //     onChange: handleChangeAutoComplete,
        //     value: timeFormat,
        //     options: TimeList,
        //     placeholder: 'Select a format',
        //     id: 'timeFormat',
        // },
        generalCountry: {
            // onChange: handleChangeCountry,
            // value: country,
            // options: countryLov,
            // id: 'country',
            // placeholder: PLACEHOLDER_FIELD_COUNTRY,
            // error: countryError,

            ...countryProps,
            id: 'country',
            error: countryError,
        },
        generalStateLabel: {
            // value: stateLabelValue,
            ...stateLabelProps,
        },
        generalState: {
            // // disabled: !country,
            // onChange: handleChangeState,
            // value: state,
            // id: 'state',
            // options: stateLov,
            // placeholder: statePlaceholder,
            // error: stateError,

            ...stateProps,
            id: 'state',
            error: stateError,
        },
        generalCity: {
            // // disabled: !state,
            // onChange: handleChangeCity,
            // value: city,
            // id: 'city',
            // options: cityLov,
            // placeholder: cityPlaceholder,
            // error: cityError,

            ...cityProps,
            error: cityError,
        },

        generalCancelButton: { isHidden: pendingAccountSetup, disabled: !isDirty, onClick: () => setConfirmModalOpen(true) }, //className: rightMarginToLeftButtonCls
        generalSaveButton: { disabled: !isDirty, loading: saveLoading, onClick: handleSaveButton, color: 'primary' },

        heading: { variant: 'h6' },
        label: { variant: 'body2' },
        buttonBox: {},
        picUploadInput: { type: 'file', hidden: true, onChange: handleChangePicUploadInput, accept: '.jpg, .png' },
        confirmDeletePhotoModal: {
            title: CONFIRM_DIALOG_TITLE_DELETE_PROFILE_PHOTO,
            onClose: () => setConfirmDeletePhotoModalOpen(!confirmDeletePhotoModalOpen),
            onConfirm: handleDeletePic,
            open: confirmDeletePhotoModalOpen,
            message: CONFIRM_DIALOG_MESSAGE_DELETE_PROFILE_PHOTO,
        },
        cropModal: {
            open: cropModalOpen,
            onClose: handleCloseCropModal,
            label: 'Profile Picture',
            actionButtonDefns: {
                cancel: {
                    disabled: false,
                    onClick: handleCloseCropModal,
                },
                save: {
                    disabled: false,
                    loading: saveLoading,
                    onClick: handleUploadPic,
                    label: 'Save Changes',
                },
            },
        },
        firstNameError: { hidden: !firstNameError },
        lastNameError: { hidden: !lastNameError },
        countryError: { hidden: !countryError },
        stateError: { hidden: !stateError },
        cityError: { hidden: !cityError },
        cropContainer: {},
        cropper: { file: tempImage, setBlob: setBlob },
        uploadLinearProgress: { variant: 'determinate', value: progress, isHidden: _isNil(progress) },

        cancelModal: {
            title: CONFIRM_DIALOG_TITLE_CANCEL,
            onClose: () => setConfirmModalOpen(false),
            onConfirm: handleClickConfirmCancelModalYesButton,
            open: confirmModalOpen,
            message: CONFIRM_DIALOG_MESSAGE_CANCEL,
        },
        badWordsDialog: {
            title: 'Inappropriate Text is not Allowed!',
            children: (
                <AfitdTypography>
                    The word(s): <b>{badWordsList} </b>
                    does not meet our community guidelines. Inappropriate content cannot be saved to this platform.
                </AfitdTypography>
            ),
            open: openBadWordsDialog,
            onClose: () => clearOutBadWords(),
        },
        badImageDialog: {
            title: 'Inappropriate Images are not Allowed!',
            children: (
                <AfitdTypography>
                    We detected <b>{badImageDialogMessage}</b> in your picture which does not meet our community guidelines. Inappropriate Images cannot be saved to this platform.
                </AfitdTypography>
            ),
            open: openBadImageDialog,
            onClose: async () => {
                setOpenBadImageDialog();
                handleCloseCropModal();
                await resetJsSeResults({ userId, setSnackbarMessage, setSnackbarErrorMessage });
            },
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};
//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const General = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles()();
    const appContext = useContext(AppContext);
    const { currentUser, userId, lovs, pendingAccountSetup } = appContext;

    const layoutContext = useContext(LayoutContext);
    const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const [confirmDeletePhotoModalOpen, setConfirmDeletePhotoModalOpen] = React.useState(false);
    const [cropModalOpen, setCropModalOpen] = React.useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    const [openBadWordsDialog, setOpenBadWordsDialog] = useState(false);
    const [openBadImageDialog, setOpenBadImageDialog] = useState(false);
    const [badImageDialogMessage, setBadImageDialogMessage] = useState([]);
    const [badWords, setBadWords] = useState({});
    const [saveLoading, setSaveLoading] = useState(false);

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [blob, setBlob] = useState(null);
    const [tempImage, setTempImage] = useState('');
    const [progress, setProgress] = useState(null);
    const [firstNameError, setFirstNameError] = useState(false);
    const [lastNameError, setLastNameError] = useState(false);
    const [countryError, setCountryError] = useState(false);
    const [stateError, setStateError] = useState(false);
    const [cityError, setCityError] = useState(false);
    // const [isPictureStatusFailed, setIsPictureStatusFailed] = useState(false);
    const [isPictureProcessing, setIsPictureProcessing] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    const [data, setData] = useState({
        firstName: '',
        lastName: '',
        middleName: '',
        // language: '',
        dateFormat: '',
        profilePicUrl: '',
    });

    const {
        // countryLov,
        // stateLov,
        // setStateLov,
        // cityLov,
        // setCityLov,
        country,
        setCountry,
        state,
        setState,
        city,
        setCity,
        cityLocation,
        setCityLocation,
        newCity,
        countryProps,
        stateLabelProps,
        stateProps,
        cityProps,
    } = useCountryStateCity({
        defaultCountryLov: _get(lovs, 'countryLov', []),
        setIsDirty,
    });

    const { /*country, province,*/ firstName, lastName, profilePicUrl, middleName } = data;

    //---------------------------------------------------------------------
    // useCallback
    //---------------------------------------------------------------------
    const resetFields = useCallback(() => {
        setData({ ...currentUser });
        setCountry(_get(currentUser, 'country', null));
        setState(_get(currentUser, 'state', null));
        setCity(_get(currentUser, 'city', null));
        setCityLocation(_get(currentUser, 'cityLocation', null));
    }, [currentUser, setCountry, setState, setCity, setCityLocation]);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        resetFields();
    }, [resetFields]);

    // Detect SightEngine Response
    useEffect(() => {
        let seResult = _get(currentUser, 'seResult');
        const uploadedPictureStatus = _get(currentUser, 'uploadedPictureStatus');

        if (seResult) extractBadContentFromResult({ seResult, setBadImageDialogMessage, setOpenBadImageDialog });

        if (uploadedPictureStatus === UPLOADED_VIDEO_STATUS_PROCESSING) setIsPictureProcessing(true);
        else setIsPictureProcessing(false);
    }, [currentUser]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const isProfilePicture = profilePicUrl || false;

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleChangeFields = evt => {
        const { name, value } = evt.target;
        setIsDirty(true);
        setData({ ...data, [name]: value });
    };

    const handleChangeAutoComplete = (i, evt) => {
        const name = i.target.id.split('-')[0];
        setIsDirty(true);
        setData({ ...data, [name]: evt });
    };

    const handleChangeDateFormat = (evt, option) => {
        const dateFormatCode = option?.code;
        setIsDirty(true);
        setData({ ...data, dateFormat: dateFormatCode });
    };

    const handleClickConfirmCancelModalYesButton = () => {
        resetFields();
        setConfirmModalOpen(false);
        setIsDirty(false);
        // setErrors(initErrors);
        setLastNameError(false);
        setFirstNameError(false);
    };

    // const handleCancelButton = () => {
    //     setData({ ...currentUser });
    //     setLastNameError(false);
    //     setFirstNameError(false);
    //     setIsDirty(false);
    // };

    const handleSaveButton = async () => {
        if (!lastName) {
            setLastNameError(true);
            setFirstNameError(false);
        }
        if (!firstName) {
            setFirstNameError(true);
            setLastNameError(false);
        }
        if (!firstName && !lastName) {
            setLastNameError(true);
            setFirstNameError(true);
        }
        if (!country) setCountryError(true);
        if (!state) setStateError(true);
        if (!city) setCityError(true);

        if (firstName && lastName && country && state && city) {
            setFirstNameError(false);
            setLastNameError(false);
            const badWordsDto = {
                firstName: data.firstName,
                lastName: data.lastName,
                middleName: data.middleName,
            };

            setSaveLoading(true);
            const response = await moderateUserTextInput({ text: JSON.stringify(badWordsDto) });

            if (!response) {
                setSnackbarErrorMessage(SNACK_BAR_MESSAGE_TEXT_MODERATION_CHECK_FAILED);
                setSaveLoading(false);
            } else if (response?.profanity?.length > 0 || response?.personal?.length > 0 || response?.link?.length > 0) {
                setOpenBadWordsDialog(true);
                setBadWords(response);
                setSaveLoading(false);
            } else {
                // await saveProfileAboutMe(params);
                updateUserData({ data: { ...data, country, state, city, cityLocation, newCity }, userId, setSnackbarMessage, setSnackbarErrorMessage });
                setSaveLoading(false);
                setIsDirty(false);
            }
        }
    };

    const clearOutBadWords = () => {
        // eslint-disable-next-line no-unused-expressions
        badWords?.profanity?.forEach(badWord => {
            badWord.toLowerCase();

            const regEx = new RegExp(badWord, 'ig');

            if (firstName.toLowerCase().includes(badWord)) {
                const filteredFirstName = firstName.replace(regEx, '');
                setData({ ...data, firstName: filteredFirstName });
            }

            if (middleName.toLowerCase().includes(badWord)) {
                const filteredMiddleName = middleName.replace(regEx, '');
                setData({ ...data, middleName: filteredMiddleName });
            }

            if (lastName.toLowerCase().includes(badWord)) {
                const filteredLastName = lastName.replace(regEx, '');
                setData({ ...data, lastName: filteredLastName });
            }
        });
        setOpenBadWordsDialog(false);
        // setBadWords({});
    };

    const handleChangePicUploadInput = evt => {
        const file = evt.target.files[0];
        if (file?.size < 5000000) {
            setTempImage(file);
            setCropModalOpen(true);
        } else {
            console.log('file is huge');
        }
    };

    const handleUploadPic = async () => {
        setSaveLoading(true);

        await uploadProfilePicture(
            blob,
            userId,
            url => setData({ ...data, profilePicUrl: url }),
            currentUser,
            setSnackbarMessage,
            setSnackbarErrorMessage,
            setProgress,
            () => {
                setCropModalOpen(false);
                setIsPictureProcessing(true);
                setSaveLoading(false);
            },
        );
    };

    const handleDeletePic = () => {
        deleteProfilePicture(userId, setSnackbarMessage, setSnackbarErrorMessage, profilePicUrl);
    };

    const handleCloseCropModal = () => {
        setCropModalOpen(false);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        isDirty,
        // setIsDirty,
        data,
        // country,
        // state,
        // city,
        countryProps,
        stateLabelProps,
        stateProps,
        cityProps,
        classes,
        handleChangeFields,
        handleSaveButton,
        handleClickConfirmCancelModalYesButton,
        handleUploadPic,
        handleDeletePic,
        handleCloseCropModal,
        // currentUser,
        confirmModalOpen,
        setConfirmModalOpen,
        confirmDeletePhotoModalOpen,
        setConfirmDeletePhotoModalOpen,
        cropModalOpen,
        setCropModalOpen,
        handleChangePicUploadInput,
        handleChangeAutoComplete,
        handleChangeDateFormat,
        firstNameError,
        lastNameError,
        setBlob,
        tempImage,
        progress,
        pendingAccountSetup,
        countryError,
        setCountryError,
        stateError,
        setStateError,
        cityError,
        setCityError,
        openBadWordsDialog,
        setOpenBadWordsDialog,
        badWords,
        setBadWords,
        clearOutBadWords,
        saveLoading,
        openBadImageDialog,
        setOpenBadImageDialog,
        badImageDialogMessage,
        isPictureProcessing,
        userId,
        setSnackbarMessage,
        setSnackbarErrorMessage,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <AppContext.Provider value={{ ...appContext }}>
            <AfitdRouterPrompt when={isDirty} />
            <AfitdTypography {...defns.heading}>General</AfitdTypography>
            <div {...defns.generalAvatarContainer}>
                {!isProfilePicture && !isPictureProcessing && (
                    <div {...defns.avatarPlaceholderBox}>
                        <AfitdAvatar {...defns.avatarPlaceholder} />
                    </div>
                )}
                {isPictureProcessing ? (
                    <div {...defns.avatarPlaceholderBox}>
                        <AfitdAvatarWithRing {...defns.loadingPictureAvatar} />
                    </div>
                ) : (
                    <>{isProfilePicture && <AfitdAvatarWithRing {...defns.profilePic} />}</>
                )}
                <div {...defns.btnBox}>
                    <div>
                        <AfitdButton {...defns.generalUploadPicButton}>
                            {defns.generalUploadPicButton.title}
                            <input {...defns.picUploadInput} />
                        </AfitdButton>
                        <AfitdButton {...defns.generalDeletePicButton}>Delete</AfitdButton>
                    </div>
                    <AfitdTypography {...defns.instructionText}>JPG and PNG files are the accepted file types.</AfitdTypography>
                </div>
            </div>

            <div>
                <div {...defns.inputBox}>
                    <AfitdTypography {...defns.label}>First Name *</AfitdTypography>
                    <AfitdTextField {...defns.generalFirstName} />
                    <span {...defns.firstNameError}>First Name is required</span>
                </div>
                <div {...defns.inputBoxContainer}>
                    <div {...defns.inputBox}>
                        <AfitdTypography {...defns.label}>Middle Name</AfitdTypography>
                        <AfitdTextField {...defns.generalMiddleName} />
                    </div>
                    <div {...defns.inputBox}>
                        <AfitdTypography {...defns.label}>Last Name *</AfitdTypography>
                        <AfitdTextField {...defns.generalLastName} />
                        <span {...defns.lastNameError}>Last Name is required</span>
                    </div>
                </div>
                {/* <div {...defns.inputBoxContainer}> */}
                {/* <div {...defns.inputBox}>
                        <AfitdTypography {...defns.label}>Language</AfitdTypography>
                        <AfitdAutocompleteLite {...defns.generalLanguage} />
                    </div> */}
                {/* </div> */}
                {/* <div {...defns.inputBoxContainer}>
                    <div {...defns.inputBox}>
                        <AfitdTypography {...defns.label}>Time Format</AfitdTypography>
                        <AftidAutocomplete {...defns.generalTimeFormat} />
                    </div>
                </div> */}
                <div {...defns.inputBoxContainer}>
                    <div {...defns.inputBox}>
                        <AfitdTypography {...defns.label}>Country *</AfitdTypography>
                        {/* <AftidAutocomplete {...defns.generalCountry} /> */}
                        <AfitdCountryAutocomplete {...defns.generalCountry} />
                        <span {...defns.countryError}>Country is required</span>
                    </div>
                    <div {...defns.inputBox}>
                        <AfitdTypography {...defns.label}>{defns.generalStateLabel.value} *</AfitdTypography>
                        {/* <AftidAutocomplete {...defns.generalProvince} /> */}
                        <AfitdStateAutocomplete {...defns.generalState} />
                        <span {...defns.stateError}>State/Province is required</span>
                    </div>
                </div>
                <div {...defns.inputBoxContainer}>
                    <div {...defns.inputBox}>
                        <AfitdTypography {...defns.label}>City *</AfitdTypography>
                        {/* <AftidAutocomplete {...defns.generalCity} /> */}
                        <AfitdCityAutocomplete {...defns.generalCity} />
                        <span {...defns.cityError}>City is required</span>
                    </div>
                    <div {...defns.inputBox}>
                        <AfitdTypography {...defns.label}>Date Format</AfitdTypography>
                        <AfitdDateFormatAutocomplete {...defns.generalDateFormat} />
                    </div>
                </div>
            </div>
            {confirmDeletePhotoModalOpen && <AfitdConfirmDialog {...defns.confirmDeletePhotoModal} />}
            {cropModalOpen && (
                <AfitdEditDialog {...defns.cropModal}>
                    <div {...defns.imgBeforeCropContainer}>
                        <AfitdProfilePicCropper {...defns.cropper} />
                    </div>
                    <div {...defns.uploadLinearProgressContainer}>
                        <AfitdLinearProgress {...defns.uploadLinearProgress} />
                    </div>
                </AfitdEditDialog>
            )}

            <div className={classes.buttonBox}>
                <AfitdButton {...defns.generalCancelButton}>Cancel</AfitdButton>
                <AfitdButton {...defns.generalSaveButton}>Save Changes</AfitdButton>
            </div>
            <AfitdConfirmDialog {...defns.cancelModal} />
            <AfitdMessageDialog {...defns.badWordsDialog} />
            <AfitdMessageDialog {...defns.badImageDialog} />
        </AppContext.Provider>
    );
};

export default General;
